import jsPDF from "jspdf";
import { formatDate } from "@project/lib/date-format";

export type EntryPassPDFData = {
  bookingRef: string;
  eventTitle: string;
  eventDate: string;
  startTime?: string;
  venue: string;
  fullName: string;
  guests: string | number;
};

function drawCalendarIcon(doc: jsPDF, x: number, y: number) {
  doc.setDrawColor(217, 119, 6); // Gold
  doc.setLineWidth(0.35);
  doc.setLineDashPattern([], 0);
  doc.rect(x, y, 3.5, 3.5);
  doc.line(x, y + 1, x + 3.5, y + 1); // top horizontal bar
  // hangers / rings
  doc.line(x + 0.8, y - 0.4, x + 0.8, y + 0.4);
  doc.line(x + 2.7, y - 0.4, x + 2.7, y + 0.4);
  // small calendar grid indicator squares
  doc.setFillColor(217, 119, 6);
  doc.rect(x + 0.8, y + 1.8, 0.5, 0.5, "F");
  doc.rect(x + 2.2, y + 1.8, 0.5, 0.5, "F");
  doc.rect(x + 0.8, y + 2.6, 0.5, 0.5, "F");
  doc.rect(x + 2.2, y + 2.6, 0.5, 0.5, "F");
}

function drawMapPinIcon(doc: jsPDF, x: number, y: number) {
  doc.setDrawColor(217, 119, 6); // Gold
  doc.setLineWidth(0.35);
  doc.setLineDashPattern([], 0);
  // draw circular head
  doc.circle(x + 1.8, y + 1.5, 1.2);
  // draw point base
  doc.line(x + 0.6, y + 1.5, x + 1.8, y + 3.6);
  doc.line(x + 3.0, y + 1.5, x + 1.8, y + 3.6);
  // draw inner pin hole
  doc.setFillColor(217, 119, 6);
  doc.circle(x + 1.8, y + 1.5, 0.4, "F");
}

export function downloadEntryPassPDF(data: EntryPassPDFData) {
  // Create a landscape page of size 160mm x 80mm
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: [160, 80],
  });

  // Colors
  const BG_COLOR: [number, number, number] = [250, 248, 245]; // Cream/Beige #FAF8F5
  const GOLD: [number, number, number] = [217, 119, 6]; // Gold #D97706
  const BLUE_BADGE: [number, number, number] = [0, 71, 149]; // Dark Blue #004795
  const TEXT_DARK: [number, number, number] = [30, 41, 59]; // Slate #1E293B
  const TEXT_MUTED: [number, number, number] = [100, 116, 139]; // Muted Slate #64748B
  const LINE_COLOR: [number, number, number] = [226, 232, 240]; // Border slate-200 #E2E8F0

  // 1. Fill background color
  doc.setFillColor(...BG_COLOR);
  doc.rect(0, 0, 160, 80, "F");

  // 2. Draw dashed border (matching the UI ticket card border)
  doc.setDrawColor(186, 210, 235); // light dashed blue border
  doc.setLineWidth(0.45);
  doc.setLineDashPattern([2.2, 1.8], 0);
  doc.roundedRect(5, 5, 150, 70, 4, 4, "D");

  // Reset dash pattern for solid lines
  doc.setLineDashPattern([], 0);

  // 3. Draw Header Label
  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.text("JIVANDHARA ENTRY PASS", 10, 15.5);

  // 4. Draw Booking Reference Badge
  const badgeW = 42;
  const badgeH = 6.8;
  const badgeX = 108;
  const badgeY = 10.5;
  
  doc.setFillColor(...BLUE_BADGE);
  doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 1.5, 1.5, "F");
  
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(data.bookingRef, badgeX + badgeW / 2, badgeY + 4.8, { align: "center" });

  // 5. Draw First Divider Line
  doc.setDrawColor(...LINE_COLOR);
  doc.setLineWidth(0.35);
  doc.line(10, 21, 150, 21);

  // 6. Draw Event Title
  doc.setTextColor(...TEXT_DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14.5);
  const titleLines = doc.splitTextToSize(data.eventTitle, 140);
  doc.text(titleLines, 10, 29.5);

  // Calculate dynamic spacing if title wraps
  const titleHeight = titleLines.length * 6.5;
  let detailY = 29.5 + titleHeight + 2;

  // 7. Draw Date and Time Field
  drawCalendarIcon(doc, 10, detailY - 3.2);
  doc.setTextColor(...TEXT_DARK);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const displayDate = data.eventDate ? formatDate(data.eventDate) : "Upcoming";
  const displayTime = data.startTime ?? "09:00 AM";
  doc.text(`${displayDate}  |  ${displayTime}`, 15.5, detailY);
  
  detailY += 7.2;

  // 8. Draw Venue Field
  drawMapPinIcon(doc, 10, detailY - 3.2);
  doc.setTextColor(...TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const venueLines = doc.splitTextToSize(data.venue, 134);
  doc.text(venueLines, 15.5, detailY);

  // 9. Draw Second Divider Line
  doc.setDrawColor(...LINE_COLOR);
  doc.setLineWidth(0.35);
  doc.line(10, 53.5, 150, 53.5);

  // 10. Draw Bottom Metadata Section
  const bottomY = 59.5;

  // Primary Guest Details
  doc.setTextColor(...TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text("Primary Guest", 10, bottomY);

  doc.setTextColor(...TEXT_DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.text(data.fullName, 10, bottomY + 5.5);

  // Total Attendees Details
  doc.setTextColor(...TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text("Total Attendees", 90, bottomY);

  doc.setTextColor(...TEXT_DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.text(`${data.guests} Person(s)`, 90, bottomY + 5.5);

  // Save the generated document
  doc.save(`EntryPass-${data.bookingRef}.pdf`);
}
